import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    TextField,
    Button,
    Typography,
    Paper,
    Alert,
    CircularProgress,
    Container,
    Snackbar,
} from '@mui/material';
import axiosInstance from '../utils/axios';
import ContainerComponent from './LoginContainer';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const navigate = useNavigate();

    const handleSnackbarClose = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    // 4. Update handleEmailChange function - remove setMessage and setError calls
    const handleEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);

        // Validate email format
        if (value && !validateEmail(value)) {
            setEmailError('Please enter a valid email address');
        } else {
            setEmailError('');
        }
    };

    // Email validation regex
    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Check if form is valid
    const isFormValid = email && validateEmail(email) && !loading;

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate email (double check)
        if (!email) {
            setSnackbar({
                open: true,
                message: 'Email is required',
                severity: 'error',
            });
            return;
        }

        if (!validateEmail(email)) {
            setEmailError('Please enter a valid email address');
            return;
        }

        setLoading(true);

        try {
            const response = await axiosInstance.post(`/auth/forget-password?email=${email}`);

            if (response.status === 200) {
                setSnackbar({
                    open: true,
                    message: 'Password reset link has been sent to your email address',
                    severity: 'success',
                });
                setEmail(''); // Clear the form
                setEmailError(''); // Clear any email errors
            }
        } catch (error) {
            console.error('Forgot password error:', error);
            if (error.response?.data?.message) {
                setSnackbar({
                    open: true,
                    message: error.response.data.message,
                    severity: 'error',
                });
            } else {
                setSnackbar({
                    open: true,
                    message: 'Failed to send reset link. Please try again.',
                    severity: 'error',
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleBackToLogin = () => {
        navigate('/login');
    };

    return (
        <ContainerComponent>
            <Box
                sx={{
                    minHeight: '100vh',
                    backgroundColor: '#f5f5f5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <Container maxWidth="sm">
                    <Paper
                        elevation={3}
                        sx={{
                            p: 4,
                            borderRadius: 2,
                            backgroundColor: 'white'
                        }}
                    >
                        {/* Logo */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                            <Box
                                component="img"
                                src="/suryalogo.svg"
                                alt="Surya Logo"
                                sx={{ width: 175 }}
                            />
                        </Box>

                        {/* Title */}
                        <Typography
                            variant="h5"
                            component="h1"
                            sx={{
                                textAlign: 'center',
                                fontWeight: 600,
                                color: 'text.primary',
                                mb: 1
                            }}
                        >
                            Forgot Password
                        </Typography>

                        <Typography
                            variant="body2"
                            sx={{
                                textAlign: 'center',
                                color: 'text.secondary',
                                mb: 3
                            }}
                        >
                            Enter your email address and we'll send you a link to reset your password
                        </Typography>

                        {/* Form */}
                        <Box component="form" onSubmit={handleSubmit}>
                            <TextField
                                fullWidth
                                label="Email Address"
                                type="email"
                                value={email}
                                onChange={handleEmailChange}
                                placeholder="Enter your email address"
                                disabled={loading}
                                error={!!emailError}
                                helperText={emailError}
                                sx={{ mb: 4 }}
                                InputProps={{
                                    sx: {
                                        fontSize: '16px',
                                        '& input': {
                                            padding: '10px 14px',
                                        },
                                    },
                                }}
                                InputLabelProps={{
                                    sx: { fontSize: '16px' },
                                }}
                                FormHelperTextProps={{
                                    sx: { fontSize: '14px' },
                                }}
                            />

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={!isFormValid}
                                sx={{
                                    backgroundColor: isFormValid ? '#f16253' : '#ccc',
                                    color: 'white',
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    py: 1.5,
                                    mb: 2,
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: isFormValid ? '#d1534b' : '#ccc',
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#ccc',
                                        cursor: 'not-allowed',
                                    },
                                }}
                            >
                                {loading ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CircularProgress size={20} color="inherit" />
                                        Sending...
                                    </Box>
                                ) : (
                                    'Send Invite'
                                )}
                            </Button>

                            <Button
                                fullWidth
                                variant="text"
                                onClick={handleBackToLogin}
                                disabled={loading}
                                sx={{
                                    height: 45,
                                    color: '#f16253',
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: '#fff2f1',
                                    },
                                }}
                            >
                                Back to Login
                            </Button>
                        </Box>
                    </Paper>
                </Container>
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </ContainerComponent>
      
    );
};

export default ForgotPassword;